/* ========== RADAR / SPIDER CHART ==========
 * Subtypes (all routed here):
 *  - Radar
 *  - Radar with Markers
 *  - Filled Radar (TODO — currently renders with semi-transparent fill)
 * ========================================== */

import { COLORS } from './utils';

export function drawRadarChart(ctx, centerX, centerY, radius, chartObj) {
  var seriesArr = chartObj && chartObj.series ? chartObj.series : [];
  if (seriesArr.length === 0) return;
  var cats = chartObj.categories || [];
  var numAxes = Math.max.apply(null, seriesArr.map(function (s) { return s.data.length; }).concat([1]));
  var angleStep = (Math.PI * 2) / numAxes;
  var allVals = seriesArr.flatMap(function (s) { return s.data; });
  var maxValue = Math.max.apply(null, allVals.concat([1]));

  // Web rings
  var rings = 4;
  for (var r = 1; r <= rings; r++) {
    var ringRadius = (r / rings) * radius;
    ctx.strokeStyle = '#D8D8D8';
    ctx.lineWidth = 0.7;
    ctx.beginPath();
    for (var ai = 0; ai <= numAxes; ai++) {
      var angle = -Math.PI / 2 + ai * angleStep;
      var px = centerX + Math.cos(angle) * ringRadius;
      var py = centerY + Math.sin(angle) * ringRadius;
      if (ai === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.stroke();
  }

  // Axis lines + labels
  for (var ai2 = 0; ai2 < numAxes; ai2++) {
    var angle2 = -Math.PI / 2 + ai2 * angleStep;
    var px2 = centerX + Math.cos(angle2) * radius;
    var py2 = centerY + Math.sin(angle2) * radius;
    ctx.strokeStyle = '#CCCCCC';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.lineTo(px2, py2);
    ctx.stroke();

    var labelR = radius + 12;
    var lx = centerX + Math.cos(angle2) * labelR;
    var ly = centerY + Math.sin(angle2) * labelR;
    ctx.fillStyle = '#555';
    ctx.font = '9px Arial';
    ctx.textAlign = Math.abs(Math.cos(angle2)) < 0.01 ? 'center' : Math.cos(angle2) > 0 ? 'left' : 'right';
    ctx.textBaseline = Math.abs(Math.sin(angle2)) < 0.01 ? 'middle' : Math.sin(angle2) > 0 ? 'top' : 'bottom';
    ctx.fillText(cats[ai2] || ('' + (ai2 + 1)), lx, ly);
  }

  // Data polygons. Three radar variants:
  //   standard — outline only, no markers
  //   marker   — outline + marker dots on each axis point
  //   filled   — solid semi-transparent fill, no outline emphasis, no markers
  var radarStyle = chartObj.radarStyle || "marker";
  seriesArr.forEach(function (series, sIdx) {
    var data = series.data;
    if (!data || data.length === 0) return;
    var color = series.color || COLORS[sIdx % COLORS.length];

    // Fill: stronger for filled-radar, lighter outline-tint for others.
    ctx.globalAlpha = radarStyle === "filled" ? 0.55 : 0.15;
    ctx.fillStyle = color;
    ctx.beginPath();
    data.forEach(function (val, i) {
      var rr = (val / maxValue) * radius;
      var ang = -Math.PI / 2 + i * angleStep;
      var ppx = centerX + Math.cos(ang) * rr;
      var ppy = centerY + Math.sin(ang) * rr;
      if (i === 0) ctx.moveTo(ppx, ppy);
      else ctx.lineTo(ppx, ppy);
    });
    ctx.closePath();
    ctx.fill();
    ctx.globalAlpha = 1;

    // Outline — skip on filled radar (the solid fill is the visual)
    if (radarStyle !== "filled") {
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      data.forEach(function (val, i) {
        var rr = (val / maxValue) * radius;
        var ang = -Math.PI / 2 + i * angleStep;
        var ppx = centerX + Math.cos(ang) * rr;
        var ppy = centerY + Math.sin(ang) * rr;
        if (i === 0) ctx.moveTo(ppx, ppy);
        else ctx.lineTo(ppx, ppy);
      });
      ctx.closePath();
      ctx.stroke();
    }

    // Markers — only when radarStyle is "marker"
    if (radarStyle === "marker") {
      data.forEach(function (val, i) {
        var rr = (val / maxValue) * radius;
        var ang = -Math.PI / 2 + i * angleStep;
        var ppx = centerX + Math.cos(ang) * rr;
        var ppy = centerY + Math.sin(ang) * rr;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(ppx, ppy, 3, 0, Math.PI * 2);
        ctx.fill();
      });
    }
  });

  // ── Ring value labels ────────────────────────────────────────────────
  // Excel writes numeric tick values along the vertical axis (top spoke)
  // at each ring level. They help readers translate ring distance into
  // actual values.
  ctx.fillStyle = '#888';
  ctx.font = '8px Arial';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  for (var ri = 1; ri <= rings; ri++) {
    var ringRadius = (ri / rings) * radius;
    var val = (ri / rings) * maxValue;
    var labelVal = val % 1 === 0 ? val.toString() : val.toFixed(1);
    // Place along the top spoke (12 o'clock), just left of the ring intersection
    ctx.fillText(labelVal, centerX - 3, centerY - ringRadius);
  }

  // ── Legend (color swatch + series name) ──────────────────────────────
  // Sits below the radar plot. Each series gets a horizontal entry —
  // matches the "Sales", "Profit" indicators Excel shows.
  ctx.font = '9px Calibri, Arial';
  ctx.textBaseline = 'middle';
  var legendItems = seriesArr.map(function (s, i) {
    var name = s.name || ('Series ' + (i + 1));
    return {
      name: name,
      color: s.color || COLORS[i % COLORS.length],
      width: 14 + ctx.measureText(name).width + 16,
    };
  });

  // Wrap legend into multiple rows if needed (canvas width = ~2*centerX)
  var legendMaxW = centerX * 1.85;
  var rowsPacked = [];
  var current = [];
  var currentW = 0;
  legendItems.forEach(function (it) {
    if (current.length > 0 && currentW + it.width > legendMaxW) {
      rowsPacked.push(current);
      current = [];
      currentW = 0;
    }
    current.push(it);
    currentW += it.width;
  });
  if (current.length > 0) rowsPacked.push(current);

  var rowH = 13;
  var legendStartY = centerY + radius + 24;
  rowsPacked.forEach(function (row, ri2) {
    var rowWidth = row.reduce(function (s, it) { return s + it.width; }, 0);
    var rowX = centerX - rowWidth / 2;
    var rowY = legendStartY + ri2 * rowH;
    row.forEach(function (item) {
      ctx.fillStyle = item.color;
      ctx.fillRect(rowX, rowY - 5, 10, 10);
      ctx.fillStyle = '#333';
      ctx.textAlign = 'left';
      ctx.fillText(item.name, rowX + 14, rowY);
      rowX += item.width;
    });
  });
}
